import React, { forwardRef, HTMLAttributes, Ref, useCallback, useEffect, useMemo, useRef, useState } from "react";
import SearchToolbar from "../SearchToolbar";
import { useI18n, t } from "@bookingcom/lingojs-react";
import useLocaleContext from "../../../hooks/useLocaleContext";
import useSearchbox from "../../../hooks/useSearchbox";
import { Container, HiddenVisually } from "@bookingcom/bui-react";
import {
  SBSearchCallbackParams,
  SearchboxController,
  SearchBoxVerticalCollapsible
} from "@bookingcom/flights-searchbox";
import styles from "./SearchHeader.module.css";
import { useStickySearchBoxExp } from "../../../utils/experiments/mdotvalue/flights_mdotvalue_sr_sticky_search_box";
import useUserAgent from "../../../hooks/useUserAgent";
import throttle from "../../../utils/throttle";
import { mcn } from "../../../utils/mergeClassnames";
import { useLocation } from "react-router-dom";
import { RouteName } from "@flights/constants";
import { useWinterDeals } from "../../../hooks/useWinterDeals";
import { trackWithDefaultStage } from "@flights/et-universal";
import useGlobalContext from "../../../hooks/useGlobalContext";

const SearchHeader = ({
  hideToolbar = false,
  onSearch,
  availableCountries,
  routeName
}: {
  hideToolbar?: boolean;
  onSearch?: (params: SBSearchCallbackParams) => void;
  availableCountries?: string[];
  routeName?: RouteName;
}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const i18n = useI18n();
  const { isRTL } = useLocaleContext();
  const searchBoxProps = useSearchbox();
  const datepickerOpen = !!Number(queryParams.get("open_datepicker"));
  const searchBoxControllerProps = {
    ...searchBoxProps,
    isDateDropdownOpen: datepickerOpen
  };

  //@start flights_mdotvalue_sr_sticky_search_box
  const { isMobile } = useUserAgent();
  const searchHeaderRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const collapseSearchBox = useRef<() => void>();
  const { getVariant, trackFlightsSearchBoxOpen } = useStickySearchBoxExp(isSticky);
  const { isWinterDealsExp } = useWinterDeals();
  const { currencyCode } = useGlobalContext();

  const isStickySearchBoxExpRunning = getVariant() > 0;

  const Wrapper = useMemo(() => {
    return forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(wrapper);

    function wrapper({ children, ...props }: HTMLAttributes<HTMLDivElement>, ref: Ref<HTMLDivElement>) {
      return (
        <>
          <div ref={ref}></div>
          {isStickySearchBoxExpRunning ? <div {...props}>{children}</div> : children}
        </>
      );
    }
  }, [isStickySearchBoxExpRunning]);

  useEffect(() => {
    if (!isMobile) return;

    const scrollHandler = throttle(() => {
      const element = searchHeaderRef.current;
      const top = element?.getBoundingClientRect().top;
      const isPastSearchHeader = top !== undefined && top <= 0;

      setIsSticky(isPastSearchHeader);
    }, 100);

    addEventListener("scroll", scrollHandler);

    return () => {
      removeEventListener("scroll", scrollHandler);
    };
  }, [isMobile]);

  useEffect(() => {
    if (isSticky && collapseSearchBox.current && isStickySearchBoxExpRunning) {
      collapseSearchBox.current();
    }
  }, [isSticky, isStickySearchBoxExpRunning]);

  const onExpand = useCallback(() => {
    trackFlightsSearchBoxOpen();
    if (isStickySearchBoxExpRunning && isSticky) {
      scrollTo(0, 0);
    }
  }, [isStickySearchBoxExpRunning, isSticky, trackFlightsSearchBoxOpen]);

  const setCollapseFn = useCallback(
    (collapseFn: () => void) => {
      if (isStickySearchBoxExpRunning) {
        collapseSearchBox.current = collapseFn;
      }
    },
    [isStickySearchBoxExpRunning]
  );
  //@end flights_mdotvalue_sr_sticky_search_box

  const verticalCollapsibleSearchbox = useCallback(
    () => (
      <SearchBoxVerticalCollapsible
        //@start flights_mdotvalue_sr_sticky_search_box
        onExpand={onExpand}
        setCollapseFn={setCollapseFn}
        isMobile={isMobile} // only for tracking stage
        //@end flights_mdotvalue_sr_sticky_search_box
      />
    ),
    [onExpand, setCollapseFn, isMobile]
  );

  return (
    <Wrapper
      className={mcn(
        styles.searchHeader,
        //@start flights_mdotvalue_sr_sticky_search_box
        isStickySearchBoxExpRunning ? styles.searchHeaderSticky : "",
        isStickySearchBoxExpRunning && isSticky ? styles.searchHeaderIsStuck : ""
        //@end flights_mdotvalue_sr_sticky_search_box
      )}
      ref={searchHeaderRef}
    >
      <div className={styles.minifiedSearchBox}>
        <div className={styles.background} />
        <HiddenVisually>
          <h1>{i18n.trans(t("a11y_flights_sr_search_hidden_heading"))}</h1>
        </HiddenVisually>
        <Container>
          <SearchboxController
            i18n={i18n}
            {...searchBoxControllerProps}
            isRTL={isRTL}
            onSearch={(searchParams) => {
              if (onSearch && typeof onSearch === "function") {
                onSearch(searchParams);
              } else {
                searchBoxProps.onSearch(searchParams);
              }
            }}
            availableCountries={availableCountries}
            routeName={routeName}
            enabledCountriesAndAnywhere={true}
            isWinterDealsExp={isWinterDealsExp}
            fareCalendarConfig={{
              isFareCalendarMobileEnabled: !!trackWithDefaultStage("flights_web_cat_fare_calendar", 1),
              currencyCode
            }}
          >
            {verticalCollapsibleSearchbox}
          </SearchboxController>
        </Container>
      </div>
      <HiddenVisually>
        <h2>{i18n.trans(t("a11y_flights_sr__results_hidden_heading"))}</h2>
      </HiddenVisually>
      {hideToolbar ? null : <SearchToolbar />}
    </Wrapper>
  );
};
export default SearchHeader;
